import React, { useRef, useState, useEffect } from "react";
import { nanoid } from "nanoid";
import api from "../../../utils/api";

interface SignedUrlResponse {
	signedUrl: string;
	publicUrl: string;
	objectName: string;
}

interface ConfirmUploadResponse {
	message?: string;
	url?: string;
	errors?: unknown;
}

// Define a type for image data
type ImageData = {
	id: string;
	file: File | null;
	previewUrl: string;
	order: number;
	objectName?: string;
	publicUrl?: string;
	uploading?: boolean;
	error?: string;
};

type FormDataWithImages = {
	images: ImageData[];
};

// Define media query helper
const useIsMobile = () => {
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkIfMobile = () => {
			setIsMobile(window.innerWidth < 768); // 768px is the md breakpoint in Tailwind
		};

		// Initial check
		checkIfMobile();

		// Add listener for window resize
		window.addEventListener("resize", checkIfMobile);

		// Clean up
		return () => window.removeEventListener("resize", checkIfMobile);
	}, []);

	return isMobile;
};

type ImageUploadProps<T extends FormDataWithImages> = {
	formData: Pick<T, "images">;
	setFormData: React.Dispatch<React.SetStateAction<T>>;
};

function ImageUpload<T extends FormDataWithImages>({
	formData,
	setFormData,
}: ImageUploadProps<T>) {
	const dropAreaRef = useRef<HTMLDivElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [selectedImage, setSelectedImage] = useState<string | null>(null);
	const [draggedId, setDraggedId] = useState<string | null>(null);
	const isMobile = useIsMobile();

	const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			processFiles(Array.from(e.target.files));
		}
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (dropAreaRef.current) {
			dropAreaRef.current.classList.add("border-violet-500");
			dropAreaRef.current.classList.add("bg-violet-50");
			dropAreaRef.current.classList.add("dark:bg-violet-900/20");
		}
	};

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (dropAreaRef.current) {
			dropAreaRef.current.classList.remove("border-violet-500");
			dropAreaRef.current.classList.remove("bg-violet-50");
			dropAreaRef.current.classList.remove("dark:bg-violet-900/20");
		}
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (dropAreaRef.current) {
			dropAreaRef.current.classList.remove("border-violet-500");
			dropAreaRef.current.classList.remove("bg-violet-50");
			dropAreaRef.current.classList.remove("dark:bg-violet-900/20");
		}

		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			processFiles(Array.from(e.dataTransfer.files));
		}
	};

	const [isProcessing, setIsProcessing] = useState(false);

	const uploadToGCS = async (image: ImageData): Promise<ImageData> => {
		if (!image.file) {
			return { ...image, error: "No file to upload" };
		}

		try {
			// Step 1: Get signed URL from backend
			const signedUrlRes = await api.post<SignedUrlResponse>("/upload/signed-url", {
				filename: image.file.name,
				contentType: image.file.type,
			});
			const { signedUrl, publicUrl, objectName } = signedUrlRes;

			// Step 2: Upload to GCS using signed URL
			// Direct fetch for GCS upload (not through our API)
			const uploadResponse = await fetch(signedUrl, {
				method: "PUT",
				headers: {
					"Content-Type": image.file.type,
				},
				body: image.file,
			});

			if (!uploadResponse.ok) {
				throw new Error("Failed to upload to GCS");
			}

			// Ensure publicUrl is properly encoded (spaces can cause issues)
			const encodedPublicUrl = encodeURI(publicUrl);

			// Step 3: Confirm upload with backend
			const confirmResponse = await api.post<ConfirmUploadResponse>(
				"/upload/confirm",
				{
					publicUrl: encodedPublicUrl,
					objectName,
				}
			);

			// Type-safe URL extraction with fallbacks
			let finalPublicUrl = publicUrl;

			// Check if confirmResponse has url property
			if (typeof confirmResponse.url === "string" && confirmResponse.url) {
				finalPublicUrl = confirmResponse.url;
			}
			// Check if there's a URL in the message property
			else if (
				typeof confirmResponse.message === "string" &&
				confirmResponse.message.includes("https://")
			) {
				const match = confirmResponse.message.match(/https:\/\/[^\s"]+/);
				if (match && match[0]) {
					finalPublicUrl = match[0];
				}
			}

			return {
				...image,
				publicUrl: finalPublicUrl,
				objectName,
				uploading: false,
			};
		} catch (error) {
			console.error("Upload error:", error);
			return {
				...image,
				uploading: false,
				error:
					error instanceof Error
						? error.message
						: "Unknown error during upload",
			};
		}
	};

	const processFiles = async (files: File[]) => {
		const imageFiles = files.filter((file) => file.type.startsWith("image/"));

		if (imageFiles.length === 0) return;

		setIsProcessing(true);

		try {
			// Create initial image objects with local preview
			const newImages = imageFiles.map((file, index) => ({
				id: nanoid(8),
				file,
				previewUrl: URL.createObjectURL(file),
				order: formData.images.length + index,
				uploading: true,
			}));

			// Add to form data immediately to show loading state
			const combinedImages = [...formData.images, ...newImages].slice(0, 10);
			setFormData((prev) => ({
				...prev,
				images: combinedImages,
			} as T));

			// Upload each image to GCS
			const updatedImages = await Promise.all(
				newImages.map((image) => uploadToGCS(image))
			);

			// Update form data with uploaded images
			// This approach ensures we replace the exact images that were being uploaded
			const finalImages = combinedImages.map((image) => {
				const updatedImage = updatedImages.find(
					(updated) => updated.id === image.id
				);
				return updatedImage || image;
			});

			setFormData((prev) => ({
				...prev,
				images: finalImages,
			} as T));
		} catch (error) {
			console.error("Error processing files:", error);
		} finally {
			setIsProcessing(false);
		}
	};

	const removeImage = (id: string) => {
		const updatedImages = formData.images
			.filter((image) => image.id !== id)
			.map((image, index) => ({ ...image, order: index }));

		setFormData((prev) => ({
			...prev,
			images: updatedImages,
		} as T));
	};

	const reorderImage = (id: string, direction: "up" | "down") => {
		const imageIndex = formData.images.findIndex((img) => img.id === id);
		if (
			(direction === "up" && imageIndex > 0) ||
			(direction === "down" && imageIndex < formData.images.length - 1)
		) {
			const newImages = [...formData.images];
			const swapIndex = direction === "up" ? imageIndex - 1 : imageIndex + 1;

			const tempOrder = newImages[imageIndex].order;
			newImages[imageIndex].order = newImages[swapIndex].order;
			newImages[swapIndex].order = tempOrder;

			newImages.sort((a, b) => a.order - b.order);

			setFormData((prev) => ({
				...prev,
				images: newImages,
			} as T));
		}
	};

	// Funciones para el reordenamiento por arrastrar y soltar
	const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
		setDraggedId(id);
		e.dataTransfer.setData("text/plain", id);
		// Establecer una vista previa semitransparente
		if (e.currentTarget.firstChild instanceof HTMLElement) {
			e.dataTransfer.setDragImage(e.currentTarget.firstChild, 20, 20);
		}
		e.currentTarget.classList.add("opacity-50");
	};

	const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
		e.currentTarget.classList.remove("opacity-50");
		setDraggedId(null);
	};

	const handleImageDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	};

	const handleImageDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.currentTarget.classList.add("bg-blue-50", "border-blue-300");
	};

	const handleImageDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.currentTarget.classList.remove("bg-blue-50", "border-blue-300");
	};

	const handleImageDrop = (
		e: React.DragEvent<HTMLDivElement>,
		targetId: string
	) => {
		e.preventDefault();
		e.stopPropagation();
		e.currentTarget.classList.remove("bg-blue-50", "border-blue-300");

		// Ignorar si se arrastra sobre sí mismo
		if (draggedId === targetId) return;

		// Encontrar índices de origen y destino
		const sourceIndex = formData.images.findIndex(
			(img) => img.id === draggedId
		);
		const targetIndex = formData.images.findIndex((img) => img.id === targetId);

		if (sourceIndex === -1 || targetIndex === -1) return;

		// Crear una copia y reordenar
		const newImages = [...formData.images];

		// Intercambiar órdenes
		const tempOrder = newImages[sourceIndex].order;
		newImages[sourceIndex].order = newImages[targetIndex].order;
		newImages[targetIndex].order = tempOrder;

		// Ordenar por la propiedad order
		newImages.sort((a, b) => a.order - b.order);

		setFormData((prev) => ({
			...prev,
			images: newImages,
		} as T));
	};

	// Clean up image preview URLs when component unmounts
	React.useEffect(() => {
		return () => {
			formData.images.forEach((image) => {
				// Only revoke locally created object URLs, not GCS URLs
				if (image.previewUrl && !image.publicUrl) {
					URL.revokeObjectURL(image.previewUrl);
				}
			});
		};
	}, [formData.images]);

	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
			<h3 className="text-lg mb-4 font-semibold text-gray-900 dark:text-white pb-2">
				画像（最大10枚）
			</h3>
			<div className="">
				<input
					type="file"
					multiple
					accept="image/*"
					onChange={handleImageUpload}
					className="hidden"
					id="image-upload"
					ref={fileInputRef}
					disabled={formData.images.length >= 10}
				/>
				<div
					ref={dropAreaRef}
					onDragOver={handleDragOver}
					onDragEnter={handleDragEnter}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={`w-full p-4 border-2 mb-4 border-dashed rounded-lg text-center transition-colors duration-200 ${
						formData.images.length >= 10
							? "border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500"
							: "border-violet-300 dark:border-violet-600 text-violet-500 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20"
					}`}
				>
					{formData.images.length >= 10 ? (
						<div className="py-4">最大枚数に達しました</div>
					) : (
						<div className="py-4">
							<svg
								className="mx-auto h-12 w-12 text-violet-300 dark:text-violet-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
								></path>
							</svg>
							<p className="mt-2 text-gray-700 dark:text-gray-300">
								画像をドラッグ&ドロップ
							</p>
							<p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
								または
							</p>
							<button
								type="button"
								onClick={() => fileInputRef.current?.click()}
								className="mt-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg hover:from-violet-600 hover:to-purple-700 transition-all"
							>
								ファイルを選択
							</button>
							<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
								最大10枚まで、JPEG、PNG、GIF形式
							</p>
						</div>
					)}
				</div>

				{formData.images.length > 0 && (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{formData.images.map((image, index) => (
							<div
								key={image.id}
								className="relative border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden group bg-white dark:bg-gray-800"
								draggable={!image.uploading && !image.error}
								onDragStart={(e) => handleDragStart(e, image.id)}
								onDragEnd={handleDragEnd}
								onDragOver={handleImageDragOver}
								onDragEnter={handleImageDragEnter}
								onDragLeave={handleImageDragLeave}
								onDrop={(e) => handleImageDrop(e, image.id)}
							>
								{image.uploading ? (
									<div className="w-full h-48 flex items-center justify-center bg-gray-100">
										<div className="flex flex-col items-center">
											<svg
												className="animate-spin h-10 w-10 text-blue-500"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
											>
												<circle
													className="opacity-25"
													cx="12"
													cy="12"
													r="10"
													stroke="currentColor"
													strokeWidth="4"
												></circle>
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												></path>
											</svg>
											<p className="mt-2 text-sm text-gray-600">
												アップロード中...
											</p>
										</div>
									</div>
								) : (
									<div
										className="w-full h-48 bg-gray-100 flex items-center justify-center relative cursor-move"
										// Removed the click handler as we now have a dedicated zoom button
									>
										<img
											src={image.publicUrl || image.previewUrl}
											alt={`イベント画像 ${index + 1}`}
											className="h-full w-auto max-w-full object-contain pointer-events-none"
											onError={(e) => {
												e.currentTarget.src =
													"https://via.placeholder.com/400x300?text=Error+Loading+Image";
											}}
										/>
										{!image.uploading && !image.error && (
											<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200">
												<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
													<div className="bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-90 text-gray-700 dark:text-gray-300 px-3 sm:px-4 py-2 rounded-lg shadow-lg text-center">
														ドラッグして順序変更
													</div>
												</div>
											</div>
										)}
									</div>
								)}

									{image.error && (
										<div className="absolute inset-0 bg-red-100 bg-opacity-80 flex items-center justify-center p-4">
											<div className="text-center">
												<div className="text-red-600 font-semibold mb-2">
													エラー
												</div>
												<div className="text-xs text-red-700">
													{image.error}
												</div>
												<button
													type="button"
													onClick={() => removeImage(image.id)}
													className="mt-2 px-3 py-1 rounded-full bg-white text-red-500 hover:bg-red-50 text-xs"
												>
													削除
												</button>
											</div>
										</div>
									)}

									<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200">
										{!image.uploading && !image.error && (
											<div
												className={`absolute bottom-2 right-2 flex space-x-2 ${
													isMobile
														? "opacity-100"
														: "opacity-0 group-hover:opacity-100"
												} transition-all duration-200`}
											>
												<button
													type="button"
													onClick={() => reorderImage(image.id, "up")}
													disabled={index === 0}
													className={`w-8 h-8 flex items-center justify-center rounded-full ${
														index === 0
															? "bg-gray-200 text-gray-400"
															: "bg-white text-blue-500 hover:bg-blue-100"
													}`}
												>
													↑
												</button>
												<button
													type="button"
													onClick={() => reorderImage(image.id, "down")}
													disabled={index === formData.images.length - 1}
													className={`w-8 h-8 flex items-center justify-center rounded-full ${
														index === formData.images.length - 1
															? "bg-gray-200 text-gray-400"
															: "bg-white text-blue-500 hover:bg-blue-100"
													}`}
												>
													↓
												</button>
												<button
													type="button"
													onClick={() =>
														setSelectedImage(
															image.publicUrl || image.previewUrl
														)
													}
													className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-blue-500 hover:bg-blue-100"
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														className="h-4 w-4"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
														/>
													</svg>
												</button>
												<button
													type="button"
													onClick={() => removeImage(image.id)}
													className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-red-500 hover:bg-red-100"
												>
													×
												</button>
											</div>
										)}
									</div>
										<div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 text-xs rounded">
											{index + 1}
										</div>
									</div>
							))}
					</div>
				)}

				{/* Modal para ampliación de imágenes */}
				{selectedImage && (
					<div
						className="fixed m-0 inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center"
						onClick={() => setSelectedImage(null)}
					>
						<button
							className="fixed top-4 right-4 w-8 h-8 flex items-center justify-center bg-white text-black rounded-full"
							onClick={(e) => {
								e.stopPropagation();
								setSelectedImage(null);
							}}
						>
							×
						</button>
						<div className="relative max-w-screen-lg max-h-screen w-full h-full flex items-center justify-center">
							<img
								src={selectedImage}
								alt="拡大画像"
								className="max-w-full max-h-full object-contain"
								onClick={(e) => e.stopPropagation()}
							/>
						</div>
					</div>
				)}

				{isProcessing && (
					<div className="mt-4 p-3 bg-blue-50 text-blue-700 rounded-lg flex items-center">
						<svg
							className="animate-spin h-5 w-5 mr-3"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
						>
							<circle
								className="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								strokeWidth="4"
							></circle>
							<path
								className="opacity-75"
								fill="currentColor"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
							></path>
						</svg>
						画像をアップロード中です。完了までお待ちください...
					</div>
				)}
			</div>
		</div>
	);
}

export default ImageUpload;
